import { DeviceSearchParams } from '../hooks/useDeviceSearch';

/**
 * FS-542 — the Devices-page view a device workspace was opened from.
 *
 * Routine review is a sequential walk of the filtered list, so the workspace has
 * to know which list it was entered from: the back arrow and the close control
 * return to exactly that view, and Previous/Next step through it in its own
 * order. The Devices page keeps its whole view in URL search params, so the
 * context that gets carried is simply that query string plus the device's
 * 0-based position in the list:
 *
 *   /devices/8/analysis?list=entity_prefix%3D%2Fresidential%2Fpilot&i=3
 *
 * Carrying it in the URL (rather than router state) means a reload, a bookmark
 * of the walk, or a Cmd-click into a new tab all keep working, and a device
 * opened by a hand-written link simply has no `list` param — nothing about the
 * list it "should" belong to is ever guessed.
 */

export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZES = [10, 20, 50, 100];

// Columns the Fleet Explorer can sort on. Allowlist: any other ?sort= value in
// the URL is normalized to unsorted so junk can't be forwarded to the backend.
export const SORT_KEYS = ['activated_at'] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export type SortDir = 'asc' | 'desc';

export interface DeviceListView {
  /** Search params for the list as a whole, ready for /devices/search. */
  params: DeviceSearchParams;
  page: number;
  pageSize: number;
  sort?: SortKey;
  dir: SortDir;
}

/**
 * Read a Devices-page query string into a search request. Shared by the Fleet
 * Explorer itself and by the workspace resolving the list it was opened from,
 * so both interpret the same URL identically — including the sort allowlist.
 */
export const readDeviceListView = (sp: URLSearchParams): DeviceListView => {
  const page = Math.max(parseInt(sp.get('page') ?? '1', 10) || 1, 1);
  const pageSize = parseInt(sp.get('page_size') ?? '', 10) || DEFAULT_PAGE_SIZE;
  const rawSort = sp.get('sort');
  const sort: SortKey | undefined = SORT_KEYS.includes(rawSort as SortKey)
    ? (rawSort as SortKey)
    : undefined;
  const dir: SortDir = sp.get('dir') === 'asc' ? 'asc' : 'desc';

  return {
    params: {
      q: sp.get('q') ?? undefined,
      entity_prefix: sp.get('entity_prefix') ?? undefined,
      context_status: sp.get('context_status') ?? undefined,
      app_owner_uid: sp.get('app_owner_uid') ?? undefined,
      app_location_id: sp.get('app_location_id') ?? undefined,
      app_hvac_id: sp.get('app_hvac_id') ?? undefined,
      sort,
      dir: sort ? dir : undefined,
      page,
      pageSize,
    },
    page,
    pageSize,
    sort,
    dir,
  };
};

/** Workspace search params carrying the list context. */
export const LIST_PARAM = 'list';
export const INDEX_PARAM = 'i';

export interface DeviceListContext {
  /** The Devices-page query string, '' for the unfiltered default view. */
  query: string;
  /** 0-based position of this device within that list. */
  index: number;
}

/**
 * The list context carried on a workspace URL, or null when there is none —
 * a direct link, a bookmark, a fresh tab. A present-but-empty `list` is a real
 * context (the unfiltered list), so absence is tested with has(), not truthiness.
 */
export const readDeviceListContext = (sp: URLSearchParams): DeviceListContext | null => {
  const query = sp.get(LIST_PARAM);
  if (query === null) return null;
  const index = parseInt(sp.get(INDEX_PARAM) ?? '', 10);
  if (!Number.isInteger(index) || index < 0) return null;
  return { query, index };
};

/** The Devices page as the device was opened from it. */
export const deviceListHref = (ctx: DeviceListContext | null): string =>
  ctx?.query ? `/devices?${ctx.query}` : '/devices';

/** A workspace URL for `deviceId` on `tab`, carrying the list context if any. */
export const workspaceHref = (
  deviceId: number | string,
  tab: string,
  ctx: DeviceListContext | null,
): string => {
  const path = `/devices/${deviceId}/${tab}`;
  if (!ctx) return path;
  const sp = new URLSearchParams();
  sp.set(LIST_PARAM, ctx.query);
  sp.set(INDEX_PARAM, String(ctx.index));
  return `${path}?${sp.toString()}`;
};

/**
 * Move the context to `index`, keeping the list's page in step with it.
 *
 * Stepping ignores page boundaries (it walks positions, not pages), but the
 * back arrow should land on the page holding the device actually being viewed
 * rather than the page the walk started on — same list, same filters, just
 * positioned where the reviewer is.
 */
export const stepDeviceListContext = (
  ctx: DeviceListContext,
  index: number,
): DeviceListContext => {
  const sp = new URLSearchParams(ctx.query);
  const { pageSize } = readDeviceListView(sp);
  const page = Math.floor(index / pageSize) + 1;
  if (page > 1) sp.set('page', String(page));
  else sp.delete('page');
  return { query: sp.toString(), index };
};
